import React, { useEffect, useMemo, useRef, useState } from "react";
import { preserveBrokenImageBox } from "../../Utils/imageUtils";
import { resolveFeedCardImage } from "../../Utils/media/feedCardImage";
import { useFeedMediaPriority } from "../../Utils/media/feedMediaPriority";

type FeedCardPictureProps = {
  url: string;
  className?: string;
  style?: React.CSSProperties;
  alt?: string;
  /** Above-the-fold tile: eager load + high fetch priority, no fade. */
  priority?: boolean;
  loading?: "eager" | "lazy";
  onLoad?: () => void;
  onError?: (event: { currentTarget: HTMLImageElement }) => void;
};

/**
 * Feed still: WebP srcset when Supabase can resize, JPEG/PNG `<img>` fallback,
 * reserved box via the caller's aspect-ratio class, gray until decoded.
 */
export function FeedCardPicture({
  url,
  className,
  style,
  alt = "",
  priority: priorityProp,
  loading,
  onLoad,
  onError,
}: FeedCardPictureProps) {
  const contextPriority = useFeedMediaPriority();
  const priority = priorityProp ?? contextPriority;
  const delivery = useMemo(
    () => resolveFeedCardImage(url, priority),
    [url, priority],
  );
  const imgRef = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) setLoaded(true);
  }, [delivery.src]);

  if (!delivery.src) return null;

  const imgLoading = loading ?? (priority ? "eager" : "lazy");

  const handleError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const picture = img.parentElement;
    if (picture?.tagName === "PICTURE") {
      picture.querySelectorAll("source").forEach((node) => node.remove());
    }
    if (onError) onError({ currentTarget: img });
    else preserveBrokenImageBox({ currentTarget: img });
    setLoaded(true);
  };

  return (
    <picture className="feed-card-picture">
      {delivery.srcSet ? (
        <source
          type="image/webp"
          srcSet={delivery.srcSet}
          sizes={delivery.sizes}
        />
      ) : null}
      <img
        ref={imgRef}
        src={delivery.srcSet ? delivery.fallbackSrc : delivery.src}
        alt={alt}
        className={[
          className,
          "feed-card-img-fade",
          priority ? "is-priority" : "",
          loaded ? "is-loaded" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={style}
        loading={imgLoading}
        decoding={priority ? "sync" : "async"}
        {...(priority
          ? ({ fetchpriority: "high" } as React.ImgHTMLAttributes<HTMLImageElement>)
          : {})}
        onLoad={() => {
          setLoaded(true);
          onLoad?.();
        }}
        onError={handleError}
      />
    </picture>
  );
}

export default FeedCardPicture;
